import { createCollectionDataService, readFitbitStudyStatus } from '../../services/fitbitCollectionDataService';
/**
 * StudyMetricsSummary - Tier-1 Glanceable Metrics Bar
 * Shows key study metrics at a glance with click-to-drill-down.
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import People from "@mui/icons-material/People";
import CheckCircle from "@mui/icons-material/CheckCircle";
import HourglassEmpty from "@mui/icons-material/HourglassEmpty";
import CloudDownload from "@mui/icons-material/CloudDownload";
import type { Participant, PendingParticipant } from "../../types";
import { needsAttention, participantStatus } from "../../utils/participantStatus";


interface MetricCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  icon,
  color,
  onClick,
}) => (
  <Card
    sx={{
      height: "100%",
      borderTop: `3px solid ${color}`,
    }}
  >
    <CardActionArea onClick={onClick} sx={{ height: "100%" }}>
      <CardContent sx={{ textAlign: "center", py: 2 }}>
        <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {label}
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

// The same status and attention rules as the participant list (utils/participantStatus).
export { needsAttention };

function isActiveParticipant(participant: Participant): boolean {
  return participantStatus(participant) === "active";
}

interface DataExtractionJob {
  status: "pending" | "running" | "completed" | "failed";
}

interface StudyMetricsSummaryProps {
  studyId?: string;
  participants: Participant[];
  pendingParticipants: PendingParticipant[];
  extractionJobs?: DataExtractionJob[];
  loading: boolean;
  onMetricClick?: (
    tabIndex: number,
    participantView?: "participant" | "pending",
  ) => void;
}

const StudyMetricsSummary: React.FC<StudyMetricsSummaryProps> = ({
  studyId,
  participants,
  pendingParticipants,
  extractionJobs = [],
  loading,
  onMetricClick,
}) => {
  const theme = useTheme();
  const hasFreshFitbit = participants.some(participant => participant.fitbitCollectionEnrollment);
  const hasUltrahuman = participants.some(participant => participant.ultrahumanCollectionEnrollment);
  const [fitbitRunning, setFitbitRunning] = useState<number | null>(null);
  useEffect(() => {
    let current = true;
    setFitbitRunning(null);
    if (!studyId || (!hasFreshFitbit && !hasUltrahuman)) return;
    const update = async () => {
      try {
        const results = await Promise.all([
          ...(hasFreshFitbit ? [readFitbitStudyStatus(studyId)] : []),
          ...(hasUltrahuman ? [createCollectionDataService('ultrahuman').readFitbitStudyStatus(studyId)] : []),
        ]);
        if (current) setFitbitRunning(results.reduce((count, result) => count + result.runningJobs, 0));
      }
      catch { if (current) setFitbitRunning(null); }
    };
    void update();
    const timer = setInterval(() => { if (!document.hidden) void update(); }, 30000);
    return () => { current = false; clearInterval(timer); };
  }, [studyId, hasFreshFitbit, hasUltrahuman]);

  if (loading) {
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[...Array(4)].map((_, i) => (
          <Grid item xs={6} sm={4} md key={i}>
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  const activeCount = participants.filter(isActiveParticipant).length;
  const runningJobs = extractionJobs.filter((j) => j.status === "running").length + (fitbitRunning || 0);

  const metrics: MetricCardProps[] = [
    {
      label: "Total Enrolled",
      value: participants.length,
      icon: <People />,
      color: theme.palette.primary.main,
      onClick: () => onMetricClick?.(0, "participant"),
    },
    {
      label: "Active",
      value: activeCount,
      icon: <CheckCircle />,
      color: theme.palette.success.main,
      onClick: () => onMetricClick?.(0, "participant"),
    },
    {
      label: "Pending",
      value: pendingParticipants.length,
      icon: <HourglassEmpty />,
      color: theme.palette.info.main,
      onClick: () => onMetricClick?.(0, "pending"),
    },
    {
      label: "Extractions Running",
      value: (hasFreshFitbit || hasUltrahuman) && fitbitRunning === null ? '—' : runningJobs,
      icon: <CloudDownload />,
      color: runningJobs > 0 ? theme.palette.primary.main : theme.palette.text.secondary,
      onClick: () => onMetricClick?.(1),
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {metrics.map((m) => (
        <Grid item xs={6} sm={4} md key={m.label}>
          <MetricCard {...m} />
        </Grid>
      ))}
    </Grid>
  );
};

export default StudyMetricsSummary;
